import { IcebergIcon, useLocalStorage } from '@tradesolution/iceberg-ui-react';
import { useEffect, useState } from 'react';
import { CompareStatus, qaCompareSimpleValue } from 'pages/QA/components/QACardDetails/utils';
import Style from '../../index.module.css';
import CompareIcon from '../../../CompareIcon';
import { Button, Collapse, Form, InputGroup, Row, Col } from 'react-bootstrap';
import { ChangeRequestValidator } from 'pages/QA/components/QACardDetails/ChangeRequestValidator';
import CopyToClipboardIcon from 'components/CopyToClipboardIcon';

interface Props {
  value: number | undefined;
  onChange: (value: number) => void;
  omsetningFromCr: number | undefined;
  error?: string;
}

const OmsetningRow = (props: Props) => {
  const [isExpandRow, setIsExpandRow] = useState<boolean>(false);
  const [compareStatus, setCompareStatus] = useState<CompareStatus>(
    qaCompareSimpleValue(props.omsetningFromCr?.toString(), props.value?.toString()),
  );
  const [crValueError, setCrValueError] = useState<string | null>(null);

  const handleExpandRowClick = () => {
    setIsExpandRow(!isExpandRow);
  };

  // Only auto-expand on differences, but don't auto-collapse when values match
  useEffect(() => {
    if (compareStatus === 'ulik' || compareStatus === 'ny') {
      setIsExpandRow(true);
    }
  }, [compareStatus]);

  useEffect(() => {
    setCompareStatus(
      qaCompareSimpleValue(props.omsetningFromCr?.toString(), props.value?.toString()),
    );
  }, [props.value, props.omsetningFromCr]);

  // Check if the value from the change request has any errors
  useEffect(() => {
    if (props.omsetningFromCr) {
      const error = ChangeRequestValidator.hasOmsetningErrors(props.omsetningFromCr);
      setCrValueError(error);
    } else {
      setCrValueError(null);
    }
  }, [props.omsetningFromCr]);

  return (
    <>
      <div className={Style.changeRequestRowHeaderContainer} onClick={handleExpandRowClick}>
        <h6 className={Style.changeRequestRowHeader}>
          {!isExpandRow && <CompareIcon compareStatus={compareStatus} size="sm" />}
          <span className={Style.changeRequestRowHeaderText}>Omsetning</span>
        </h6>
        <Button variant="link" size="sm">
          <IcebergIcon icon={isExpandRow ? 'chevron-up' : 'chevron-down'} bold />
        </Button>
      </div>
      <Collapse in={isExpandRow}>
        <div>
          <Row className={Style.formGroupRow}>
            <Col xs={12} md={5}>
              <Form.Group className={Style.formGroup}>
                <InputGroup size="sm" className={Style.inputGroup}>
                  <Form.Control
                    value={props.omsetningFromCr || ''}
                    readOnly
                    isValid={!crValueError && !!props.omsetningFromCr}
                    isInvalid={!!crValueError}
                  />
                  <InputGroup.Text>
                    <CopyToClipboardIcon val={props.omsetningFromCr} />
                  </InputGroup.Text>
                  <Form.Control.Feedback type="invalid">
                    {crValueError ?? 'Omsetning er ugyldig.'}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>
            </Col>
            <Col xs="auto" md={2} className={Style.compareIconContainer}>
              {!!crValueError ? (
                <CompareIcon compareStatus="ugyldig" tooltipText={crValueError} />
              ) : (
                <CompareIcon
                  compareStatus={compareStatus}
                  onClick={() => props.onChange(props.omsetningFromCr)}
                />
              )}
            </Col>
            <Col xs={12} md={5}>
              <Form.Group className={Style.formGroup}>
                <InputGroup size="sm" className={Style.inputGroup}>
                  <Form.Control
                    type="number"
                    value={props.value ?? ''}
                    onChange={(e) =>
                      props.onChange(e.target.value ? parseInt(e.target.value.trim()) : undefined)
                    }
                  />
                </InputGroup>
                {props.error && <Form.Text className="text-danger">{props.error}</Form.Text>}
              </Form.Group>
            </Col>
          </Row>
        </div>
      </Collapse>
    </>
  );
};

export default OmsetningRow;
